import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useIntlWrapperContext } from '../../Helpers/IntlWrapperProvider';
import useObjectHelper from '../../Helpers/useObjectHelper';
import { positions, researchFields } from '../../Helpers/StaticData';

import { Filters, FilteringTypes } from './BackofficeUsers.model';
import { Field } from '../../components/FiltersForm/FiltersForm.model';
import useFetchWrapper from '../../Helpers/useFetchWrapper';
import { useAuthenticationDataContext } from '../../Helpers/AuthenticationDataProvider';

export const pageSize = 10;

const useBackofficeUsers = () => {
	const [activeFilters, setActiveFilters] = useState<Filters | null>(null);
	const [activeKey, setActiveKey] = useState<string>('1');
	const [showMobileFilters, setShowMobileFilters] = useState(false);
	const [showExportPopup, setShowExportPopup] = useState(false);
	
	const { i18nLanguages } = useIntlWrapperContext();
	const { arrToOptionList, cleanObj, objToOptionList } = useObjectHelper();

	const intl = useIntl();

	const { get } = useFetchWrapper();

	const { email } = useAuthenticationDataContext();

	const filterDefaultValues: Record<FilteringTypes, any> = {
    currentPosition: undefined,
    institution: undefined,
    languages: [],
    researchAreas: [],
		role: undefined,
		status: undefined
  };

	const filterFields: Field<Record<FilteringTypes, any>>[] = [{
		label: intl.formatMessage({ id: 'formLabels.userType' }),
		name: 'role',
		options: { optionList: arrToOptionList(['Mentor', 'Mentee', "ApplicantMentee"]) },
		placeholder: intl.formatMessage({ id: 'formPlaceholders.userType'}),
		type: 'select',
	}, {
		name: 'currentPosition',
		options: { optionList: arrToOptionList(positions) },
		type: 'select',
	}, {
		name: 'institution',
		type: 'input',
	}, {
		name: 'researchAreas',
		options: { optionList: arrToOptionList(researchFields) },
		type: 'multiple',
	}, {
		name: 'languages',
		options: { optionList: objToOptionList(i18nLanguages), type: 'codeBased' },
		type: 'multiple',
		},];
	
	const ratingFilterFileds: Field<Record<FilteringTypes, any>>[] = [{
		label: intl.formatMessage({ id: 'formLabels.status' }),
		name: 'status',
		options: { alternateTranslationPrefix: 'status.rating', optionList: arrToOptionList(['Assigned', 'Accepted', 'Unassigned', 'Rejected'])},
		type: 'select'
	}]

	const getFilterFields = () => {
		if (activeKey === "2") return [filterFields[0]];
		if (activeKey === "4") return ratingFilterFileds;
		return filterFields;
	}

	const getFiltersAsQueryParams = (obj: Filters | null) => {
		return obj ? Object.keys(obj).reduce((acc: string[], curr: string) => {
			if (curr !== 'mentorsOnly') {
				acc = [...acc, `${curr}=${obj[curr]}`];
			} else if (obj[curr]) {
				acc = [...acc, `role=Mentor`]
			}
			return acc;
		}, []).join('&'): '';
	}

	const onChangeViewMentorsOnly = async(e: any) => {
		const filters: Filters = {
			...activeFilters,
			mentorsOnly: e.target.checked 
		};

		setActiveFilters(filters);
	}

	const onChangeViewMyRatingsOnly = async(e: any) => {
		const filters: Filters = {
			...activeFilters,
			myRatingsOnly: e.target.checked 
		};

		setActiveFilters(filters);
	}

	const onClickTab = (activeKey: string) => {
		setActiveKey(activeKey);
	}

	const onSearchByTerm = async(value: string) => {
		const filters: Filters = {
			...activeFilters,
			name: value,
		};

		setActiveFilters(filters);
	}

	const onResetFilters = async(callBack?: () => void) => {
		let filters: Filters = activeKey !== '4' ? {
			mentorsOnly: activeFilters? activeFilters.mentorsOnly : false,
			name: activeFilters?.name || '',
		} : {
			myRatingsOnly: activeFilters?.myRatingsOnly || false
		};
		setActiveFilters(filters);
		callBack && callBack();
	}

	const onSubmitFilters = async (data: Filters, callBack?: () => void) => {
		const filters: Filters = activeKey !== '4' ? {
			mentorsOnly: activeFilters?.mentorsOnly || false,
			name: activeFilters?.name || '',
			...cleanObj(data)
		} : { myRatingsOnly: activeFilters?.myRatingsOnly ||  false,...cleanObj(data) };
		
		setActiveFilters(filters);

		callBack && callBack();
	}

	const onExportUsers = async () => {
		const response = await get('/user/export-users');
		if (response.status === 200) toggleExportPopup();
	}

	const toggleMobileFilters = () => {
		setShowMobileFilters((currentValue) => !currentValue);
	}

	const toggleExportPopup = () => {
		setShowExportPopup((currentValue) => !currentValue);
	}

	return {
    getFiltersAsQueryParams,
		onChangeViewMentorsOnly,
		onChangeViewMyRatingsOnly,
		onClickTab,
		onExportUsers,
    onResetFilters,
    onSearchByTerm,
    onSubmitFilters,
    state: {
      activeFilters,
			activeKey,
			email,
      filterDefaultValues,
			filterFields: getFilterFields(),
			showExportPopup,
      showMobileFilters
		},
		toggleExportPopup,
    toggleMobileFilters
  };
}

export default useBackofficeUsers;